import type { LiveComposerEvent, StaffNumber } from '../../CreateMusicXml';

/** A score event with its timeline position resolved. */
export type RollEvent = Pick<LiveComposerEvent, 'id' | 'midiNotes' | 'velocities' | 'staff'> & {
  startDivisions: number;
  durationDivisions: number;
};

export type RollPedalEvent = { positionDivisions: number; pedalDown: boolean; value?: number };

export type PianoRollLayout = {
  pxPerDivision: number;
  rowHeight: number;
  topMidi: number;
  bottomMidi: number;
  keyboardWidth: number;
  gridWidth: number;
  gridHeight: number;
  velocityLaneHeight: number;
  pedalLaneHeight: number;
};

export type RollNoteRect = {
  eventId: string;
  midi: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Captured MIDI velocity, or null when the note was never played with one. */
  velocity: number | null;
  staff?: StaffNumber;
};

export type PedalSegment = { x: number; width: number; down: boolean; value: number };

const LOWEST_PIANO_MIDI = 21;
const HIGHEST_PIANO_MIDI = 108;
const DEFAULT_TOP_MIDI = 84;
const DEFAULT_BOTTOM_MIDI = 48;
const PITCH_PADDING = 2;
const MIN_ROWS = 24;
const BASE_PX_PER_DIVISION = 6;
const ROW_HEIGHT = 7;
const KEYBOARD_WIDTH = 44;
const VELOCITY_LANE_HEIGHT = 56;
const PEDAL_LANE_HEIGHT = 16;
/** Playback treats an unknown velocity as 0.8, so the lane shows the same. */
export const UNKNOWN_VELOCITY_DISPLAY = 102;

export const clampVelocity = (velocity: number) => Math.max(1, Math.min(127, Math.round(velocity)));

/** The pitch range to show: the music plus a little air, never past the keyboard. */
export function computePianoRollLayout(
  events: RollEvent[],
  totalDivisions: number,
  options: { zoom?: number } = {},
): PianoRollLayout {
  const pitches = events.flatMap((event) => event.midiNotes);
  let topMidi = DEFAULT_TOP_MIDI;
  let bottomMidi = DEFAULT_BOTTOM_MIDI;
  if (pitches.length > 0) {
    topMidi = Math.min(HIGHEST_PIANO_MIDI, Math.max(...pitches) + PITCH_PADDING);
    bottomMidi = Math.max(LOWEST_PIANO_MIDI, Math.min(...pitches) - PITCH_PADDING);
  }
  while (topMidi - bottomMidi + 1 < MIN_ROWS) {
    if (topMidi < HIGHEST_PIANO_MIDI) topMidi += 1;
    if (bottomMidi > LOWEST_PIANO_MIDI && topMidi - bottomMidi + 1 < MIN_ROWS) bottomMidi -= 1;
    if (topMidi === HIGHEST_PIANO_MIDI && bottomMidi === LOWEST_PIANO_MIDI) break;
  }
  const pxPerDivision = BASE_PX_PER_DIVISION * Math.max(0.5, options.zoom ?? 1);
  return {
    pxPerDivision,
    rowHeight: ROW_HEIGHT,
    topMidi,
    bottomMidi,
    keyboardWidth: KEYBOARD_WIDTH,
    gridWidth: Math.max(1, totalDivisions) * pxPerDivision,
    gridHeight: (topMidi - bottomMidi + 1) * ROW_HEIGHT,
    velocityLaneHeight: VELOCITY_LANE_HEIGHT,
    pedalLaneHeight: PEDAL_LANE_HEIGHT,
  };
}

export const divisionsToX = (layout: PianoRollLayout, divisions: number) =>
  layout.keyboardWidth + divisions * layout.pxPerDivision;

export const xToDivisions = (layout: PianoRollLayout, x: number) =>
  Math.max(0, (x - layout.keyboardWidth) / layout.pxPerDivision);

export const midiToY = (layout: PianoRollLayout, midi: number) =>
  (layout.topMidi - midi) * layout.rowHeight;

export const yToMidi = (layout: PianoRollLayout, y: number) =>
  Math.max(
    layout.bottomMidi,
    Math.min(layout.topMidi, layout.topMidi - Math.floor(y / layout.rowHeight)),
  );

/** One rectangle per sounding pitch; a chord becomes a stack sharing one event id. */
export function buildRollNoteRects(events: RollEvent[], layout: PianoRollLayout): RollNoteRect[] {
  return events.flatMap((event) =>
    event.midiNotes.map((midi) => ({
      eventId: event.id,
      midi,
      x: divisionsToX(layout, event.startDivisions),
      y: midiToY(layout, midi),
      width: Math.max(2, event.durationDivisions * layout.pxPerDivision - 1),
      height: layout.rowHeight - 1,
      velocity: event.velocities?.[midi] ?? null,
      staff: event.staff,
    })),
  );
}

/** Sustain as held spans, so the lane reads like the pedal under a pianist's foot. */
export function buildPedalSegments(
  pedals: RollPedalEvent[],
  totalDivisions: number,
  layout: PianoRollLayout,
): PedalSegment[] {
  const ordered = [...pedals].sort((a, b) => a.positionDivisions - b.positionDivisions);
  const segments: PedalSegment[] = [];
  let downSince: RollPedalEvent | null = null;
  for (const pedal of ordered) {
    if (pedal.pedalDown && !downSince) {
      downSince = pedal;
    } else if (!pedal.pedalDown && downSince) {
      segments.push({
        x: divisionsToX(layout, downSince.positionDivisions),
        width: Math.max(
          1,
          (pedal.positionDivisions - downSince.positionDivisions) * layout.pxPerDivision,
        ),
        down: true,
        value: downSince.value ?? 127,
      });
      downSince = null;
    }
  }
  if (downSince)
    segments.push({
      x: divisionsToX(layout, downSince.positionDivisions),
      width: Math.max(1, (totalDivisions - downSince.positionDivisions) * layout.pxPerDivision),
      down: true,
      value: downSince.value ?? 127,
    });
  return segments;
}

/** The lane's top edge is the loudest touch and its bottom the lightest. */
export const velocityFromLaneY = (y: number, laneHeight: number) =>
  clampVelocity(127 - (Math.max(0, Math.min(laneHeight, y)) / Math.max(1, laneHeight)) * 126);

export const velocityBarHeight = (velocity: number | null, laneHeight: number) =>
  Math.max(2, ((velocity ?? UNKNOWN_VELOCITY_DISPLAY) / 127) * laneHeight);

const NOTE_NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
export const isBlackKey = (midi: number) => [1, 3, 6, 8, 10].includes(midi % 12);
export const pitchLabel = (midi: number) => `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
